import * as React from 'react';

import { isRunningInstalled, useInstallPrompt } from './install-prompt';
import { installMechanism, isAppleTouchPlatform, type InstallMechanism } from './supported-platforms';

/**
 * When the install offer is raised, and the three rules that decide it (`PLT-008` AC-5, AC-7).
 *
 * ## What was already built, and what was deliberately left
 *
 * `MEM-040` AC-4 caught the browser's `beforeinstallprompt` and refused to surface it —
 * `install-prompt.ts` holds the event and prompts only from a member's own tap — and `MEM-043`
 * put the one deliberate control on My Account, where a member who went looking finds it. Both
 * files say in their headers that the *timing* is this story's, and that building a banner then
 * would have been building the thing `PLT-008` has to take apart. This is the timing, and the
 * capture underneath it is untouched.
 *
 * ## The three rules, and why each one is separately load-bearing
 *
 * AC-5: *once after the member's first completed registration, at most twice in total, and never
 * once installed.*
 *
 *   - **Never before a completed registration.** A member thirty seconds into their first visit
 *     has no reason to want this on their home screen, and the honest answer to a prompt with no
 *     reason is No — permanently, because on Chromium a dismissed install prompt is not re-offered
 *     and on iOS the member simply never opens the Share menu again. The story's Notes put it
 *     plainly: a member who has just registered *has seen the product work*.
 *   - **At most twice, ever.** Not twice per event, not twice per session, not twice per device
 *     that happens to have been cleared. Twice.
 *   - **Never once installed.** An installed app offering to install itself is a bug that reads
 *     as one.
 *
 * AC-7 is the other half of the same rule: a member who says no loses nothing. There is no
 * feature behind this offer. The only thing declining costs is push on iOS, which is a platform
 * fact rather than a consequence this product chose, and which is why the iOS copy states it
 * (AC-6) rather than leaving the member to find out by turning notifications on and hearing
 * nothing.
 *
 * ## Where the count lives, and why that is defensible
 *
 * On the device, in `localStorage`. It is a **UI preference** — how many times this browser has
 * shown a banner — and not member data: `MEM-042` AC-1's rule is that no member's connections,
 * matches or follow-ups may live there as the source of truth, and a banner count is none of
 * those. `push-registration.ts` already remembers a dismissal the same way.
 *
 * The cost is real and worth naming: a member who signs in on a second device can be offered
 * twice there too. That is the correct trade — the alternative is a per-member counter on the
 * account, which is an API change in a domain this story does not own, to make a banner slightly
 * rarer on a device where the app is genuinely not yet installed. **A member who has installed
 * the app is never offered on that device regardless of the count**, which is the case the count
 * would otherwise get wrong.
 */

/** `PLT-008` AC-5's cap, stated once. Twice, in total, for the life of this browser profile. */
export const INSTALL_OFFER_LIMIT = 2;

/** Where the count lives. See the header for why the device is the right place for it. */
export const INSTALL_OFFER_COUNT_KEY = 'h2hspark.install-offer-count';

export interface InstallOfferInput {
  /** How this browser installs, or `none` when it does not — including when it already has. */
  readonly mechanism: InstallMechanism;
  /** How many times this browser has already shown the offer. */
  readonly timesOffered: number;
  /**
   * The member has completed a registration.
   *
   * **This is the criterion**, and it is the caller's to supply because it is a fact about the
   * screen rather than about the browser: the offer is raised from a confirmed registration, so
   * a caller that can render it is a caller for whom this is true.
   */
  readonly hasCompletedRegistration: boolean;
}

/**
 * Whether to raise the offer, as a pure function.
 *
 * Every reason not to returns the same silence, in the shape `decidePushOffer` established. The
 * ordering carries no meaning and must not acquire any — a ranked list invites a "well, in *this*
 * case" exception, and the exception is always the one that fires at the wrong moment.
 */
export function decideInstallOffer(input: InstallOfferInput): boolean {
  if (input.mechanism === 'none') return false;
  if (!input.hasCompletedRegistration) return false;
  if (input.timesOffered >= INSTALL_OFFER_LIMIT) return false;
  return true;
}

/** Reads the count. A browser that refuses `localStorage` reads as "never offered". */
export function readInstallOfferCount(): number {
  try {
    const raw = window.localStorage.getItem(INSTALL_OFFER_COUNT_KEY);
    const parsed = Number.parseInt(raw ?? '', 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

/** Records that the offer was shown. Silent on a storage that refuses; see the header. */
export function recordInstallOffered(count: number): void {
  try {
    window.localStorage.setItem(INSTALL_OFFER_COUNT_KEY, String(count));
  } catch {
    // A member in private browsing may be offered again next session. That is the whole cost.
  }
}

export interface InstallOffer {
  /** Draw the offer. Already the whole of {@link decideInstallOffer}. */
  readonly shouldOffer: boolean;
  /**
   * How to install on this browser: `prompt` draws a button, `manual-ios` draws the Share
   * instruction. `none` never reaches a caller, because `shouldOffer` is false then.
   */
  readonly mechanism: InstallMechanism;
  /** Raises the browser's dialogue. Chromium only, and only from a member's own tap. */
  readonly install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  /** Takes the offer down. Nothing else in the app changes — AC-7. */
  readonly dismiss: () => void;
}

/**
 * The offer, wired to the capture `MEM-040` already built.
 *
 * ## The count is spent when the offer is *drawn*, not when it is answered
 *
 * The effect below increments once per mount, the first time `shouldOffer` is true. Spending it
 * on the answer instead would mean a member who scrolls past the banner without touching it has
 * been shown it an unbounded number of times while the counter says zero — which is the cap not
 * existing. A member sees this banner at most twice; what they do with it is theirs.
 */
export function useInstallOffer(parameters: {
  readonly hasCompletedRegistration: boolean;
}): InstallOffer {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [timesOffered, setTimesOffered] = React.useState(INSTALL_OFFER_LIMIT);
  const [dismissed, setDismissed] = React.useState(false);
  const counted = React.useRef(false);

  // Starts at the limit and is relaxed after mount, so the first paint of a member who has
  // already been offered twice never flashes a banner they have retired.
  React.useEffect(() => {
    setTimesOffered(readInstallOfferCount());
  }, []);

  const mechanism = installMechanism({
    canPrompt: canInstall,
    alreadyInstalled: isRunningInstalled(),
    appleTouch: typeof navigator === 'undefined' ? false : isAppleTouchPlatform(navigator),
  });

  const eligible = decideInstallOffer({
    mechanism,
    timesOffered,
    hasCompletedRegistration: parameters.hasCompletedRegistration,
  });

  React.useEffect(() => {
    if (!eligible || counted.current) return;
    counted.current = true;
    recordInstallOffered(timesOffered + 1);
  }, [eligible, timesOffered]);

  const install = React.useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    const outcome = await promptInstall();
    setDismissed(true);
    return outcome;
  }, [promptInstall]);

  const dismiss = React.useCallback((): void => {
    setDismissed(true);
  }, []);

  return { shouldOffer: eligible && !dismissed, mechanism, install, dismiss };
}
